import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Zap, Package, TrendingUp } from "@/components/icons";
import { formatNumber } from "@/utils/formatters";
import type { CreditBalance } from "@/types/credits";

interface CreditBalanceOverviewProps {
  balance: CreditBalance;
  usagePercentage: number;
}

export const CreditBalanceOverview: React.FC<CreditBalanceOverviewProps> = React.memo(({
  balance,
  usagePercentage
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="relative overflow-hidden glassmorphism shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Current Balance</CardTitle>
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-blue-900 dark:text-blue-100 mb-1">
            {formatNumber(balance.current)}
          </div>
          <p className="text-sm text-blue-600 dark:text-blue-400">
            Credits available
          </p>
        </CardContent>
      </Card>

      <Card className="relative overflow-hidden glassmorphism shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/10 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">Total Credits</CardTitle>
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <Package className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-green-900 dark:text-green-100 mb-1">
            {formatNumber(balance.total)}
          </div>
          <p className="text-sm text-green-600 dark:text-green-400">
            Total purchased
          </p>
        </CardContent>
      </Card>

      <Card className="relative overflow-hidden glassmorphism shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/10 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300">Usage Rate</CardTitle>
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-purple-900 dark:text-purple-100 mb-2">
            {usagePercentage.toFixed(1)}%
          </div>
          <Progress value={usagePercentage} className="h-2 mb-2" />
          <p className="text-sm text-purple-600 dark:text-purple-400">
            Credits used
          </p>
        </CardContent>
      </Card>
    </div>
  );
});